<template>
    <b-modal
        v-model="modalVisible"
        title="Новый сотрудник"
        size="lg"
        scrollable
        hide-footer
        modal-class="workforce-viewport-modal"
        :no-close-on-backdrop="saving"
        :no-close-on-esc="saving"
    >
        <b-alert v-if="errorMessage" show variant="danger">
            {{ errorMessage }}
        </b-alert>
        <div class="employee-create-grid">
            <b-form-group label="Фамилия">
                <b-form-input
                    v-model.trim="form.last_name"
                    :disabled="saving"
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Имя">
                <b-form-input
                    v-model.trim="form.first_name"
                    :disabled="saving"
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Отчество">
                <b-form-input
                    v-model.trim="form.middle_name"
                    :disabled="saving"
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Дата рождения">
                <b-form-input
                    v-model="form.birth_date"
                    type="date"
                    :disabled="saving"
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Гражданство">
                <employee-citizenship-select
                    v-model="form.citizenship"
                    :disabled="saving"
                />
            </b-form-group>
            <b-form-group label="Форма найма">
                <b-form-select v-model="form.hiring_form" :disabled="saving">
                    <b-form-select-option :value="null">Не указано</b-form-select-option>
                    <b-form-select-option value="staff">Штат</b-form-select-option>
                    <b-form-select-option value="gph">ГПХ</b-form-select-option>
                </b-form-select>
            </b-form-group>
            <b-form-group label="Статус">
                <b-form-radio-group
                    v-model="form.lifecycle_status"
                    :disabled="saving"
                    stacked
                >
                    <b-form-radio value="active">
                        <strong>Действующий</strong>
                        <small class="d-block text-muted">
                            Подтверждаю, что сотрудник принят и работает.
                        </small>
                    </b-form-radio>
                    <b-form-radio value="unverified" class="mt-2">
                        <strong>Непроверенный</strong>
                        <small class="d-block text-muted">
                            Данные сотрудника требуют уточнения.
                        </small>
                    </b-form-radio>
                </b-form-radio-group>
            </b-form-group>
            <b-form-group label="Приоритет надёжности">
                <b-form-input
                    v-model="form.reliability_priority"
                    type="number"
                    min="1"
                    :disabled="saving"
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Отдел">
                <b-form-select
                    v-model="form.operational_department_id"
                    :options="departmentOptions"
                    :disabled="saving"
                ></b-form-select>
            </b-form-group>
            <b-form-group label="Фактическая должность">
                <b-form-input
                    v-model.trim="form.operational_position"
                    :disabled="saving"
                ></b-form-input>
            </b-form-group>
        </div>

        <employee-contact-editor
            ref="contactEditor"
            v-model="form.contact_methods"
            class="mb-3"
            :disabled="saving"
            title="Контактные данные"
            hint="Необязательно. Эти контакты не создают учётную запись и не используются для авторизации."
            @input="onContactsInput"
            @validation="contactValidationError = $event"
        ></employee-contact-editor>

        <b-alert v-if="duplicates.length" show variant="warning">
            <strong>Найдены похожие карточки:</strong>
            <ul class="mb-2 mt-2">
                <li
                    v-for="employee in duplicates"
                    :key="employee.employee_id"
                >
                    <router-link
                        :to="`/employees/${employee.employee_id}`"
                        target="_blank"
                    >
                        {{ employee.full_name }}
                    </router-link>
                    <span v-if="employee.birth_date">
                        · {{ formatDate(employee.birth_date) }}
                    </span>
                    · {{ statusLabel(employee.lifecycle_status) }}
                    <ul
                        v-if="employee.match_reasons && employee.match_reasons.length"
                        class="mb-1 mt-1"
                    >
                        <li
                            v-for="reason in employee.match_reasons"
                            :key="reason"
                        >
                            {{ reason }}
                        </li>
                    </ul>
                </li>
            </ul>
            <b-form-checkbox v-model="duplicateConfirmed">
                Это другой человек. Всё равно создать новую карточку.
            </b-form-checkbox>
        </b-alert>

        <div class="d-flex justify-content-end">
            <b-button
                variant="outline-secondary"
                class="mr-2"
                :disabled="saving"
                @click="modalVisible = false"
            >
                Отмена
            </b-button>
            <b-button
                variant="success"
                :disabled="saveDisabled"
                @click="save"
            >
                <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                Создать карточку
            </b-button>
        </div>
    </b-modal>
</template>

<script>
import api from '@/utils/api';
import EmployeeContactEditor from '@/components/EmployeeContactEditor.vue';
import EmployeeCitizenshipSelect from './EmployeeCitizenshipSelect.vue';

function emptyForm() {
    return {
        last_name: '',
        first_name: '',
        middle_name: '',
        birth_date: '',
        citizenship: '',
        hiring_form: null,
        lifecycle_status: '',
        reliability_priority: '',
        operational_department_id: null,
        operational_position: '',
        contact_methods: []
    };
}

export default {
    name: 'EmployeeCreateModal',
    components: {EmployeeContactEditor, EmployeeCitizenshipSelect},
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            saving: false,
            errorMessage: '',
            contactValidationError: null,
            duplicates: [],
            duplicateConfirmed: false,
            form: emptyForm()
        };
    },
    computed: {
        modalVisible: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        departmentOptions() {
            const options = [{value: null, text: 'Не указан'}];
            return options.concat(
                (this.$store.state.departments || [])
                    .filter(department => department.is_active !== false)
                    .map(department => ({
                        value: Number(department.id || department.department_id),
                        text: department.name
                    }))
                    .filter(option => option.value)
                    .sort((left, right) => left.text.localeCompare(right.text, 'ru'))
            );
        },
        saveDisabled() {
            return this.saving
                || !this.form.last_name
                || !this.form.first_name
                || !this.form.lifecycle_status
                || Boolean(this.contactValidationError)
                || (this.duplicates.length > 0 && !this.duplicateConfirmed);
        }
    },
    watch: {
        value(visible) {
            if (visible) this.prepare();
        }
    },
    methods: {
        prepare() {
            this.form = emptyForm();
            this.errorMessage = '';
            this.contactValidationError = null;
            this.duplicates = [];
            this.duplicateConfirmed = false;
            this.$store.dispatch('getDepartments').catch(() => {});
        },
        onContactsInput(contactMethods) {
            this.form.contact_methods = contactMethods;
            this.duplicates = [];
            this.duplicateConfirmed = false;
        },
        async save() {
            if (this.saveDisabled) return;
            let contactMethods;
            try {
                contactMethods = this.$refs.contactEditor
                    ? this.$refs.contactEditor.preparedContacts()
                    : [];
            } catch (error) {
                this.errorMessage = error.message || 'Проверьте контактные данные.';
                return;
            }
            this.saving = true;
            this.errorMessage = '';
            try {
                if (!this.duplicateConfirmed) {
                    const duplicateResponse = await api.get(
                        '/api/employees/duplicate-candidates',
                        {
                            params: {
                                last_name: this.form.last_name,
                                first_name: this.form.first_name,
                                birth_date: this.form.birth_date || undefined,
                                contact_methods: contactMethods.length
                                    ? JSON.stringify(contactMethods)
                                    : undefined
                            },
                            ignoreModal: true
                        }
                    );
                    this.duplicates = duplicateResponse
                        && duplicateResponse.data
                        && Array.isArray(duplicateResponse.data.employees)
                        ? duplicateResponse.data.employees
                        : [];
                    if (this.duplicates.length) return;
                }
                const payload = Object.assign({}, this.form, {
                    birth_date: this.form.birth_date || null,
                    citizenship: this.form.citizenship || null,
                    reliability_priority: this.form.reliability_priority || null,
                    operational_department_id: this.form.operational_department_id || null,
                    operational_position: this.form.operational_position || null,
                    contact_methods: contactMethods,
                    confirm_active_status: this.form.lifecycle_status === 'active',
                    confirm_duplicate: this.duplicateConfirmed
                });
                const response = await api.post(
                    '/api/employees',
                    payload,
                    {ignoreModal: true}
                );
                this.modalVisible = false;
                this.$emit('created', response && response.data || {});
            } catch (error) {
                const data = error && error.response ? error.response.data : null;
                const duplicates = data && data.details
                    && Array.isArray(data.details.duplicates)
                    ? data.details.duplicates
                    : [];
                if (duplicates.length) this.duplicates = duplicates;
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось создать сотрудника.'
                );
            } finally {
                this.saving = false;
            }
        },
        statusLabel(status) {
            return {
                active: 'Действующий',
                reserve: 'Резерв',
                unverified: 'Непроверенный'
            }[status] || status || 'Не указан';
        },
        formatDate(value) {
            if (!value) return '—';
            const match = String(value).slice(0, 10).match(
                /^(\d{4})-(\d{2})-(\d{2})$/
            );
            return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object' && !(data instanceof Blob)) {
                return data.msg || data.message || fallback;
            }
            return typeof data === 'string' && data ? data : fallback;
        }
    }
};
</script>

<style scoped>
.employee-create-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 10px;
}

@media (max-width: 767px) {
    .employee-create-grid {
        grid-template-columns: 1fr;
    }
}
</style>

<style src="./workforce-viewport-modal.css"></style>
